import { capitalize } from 'lodash';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { User } from '~/components/Shared/User/User';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { TableContextType } from '~/enums/TableContextType';
import { useTableSettings } from '~/hooks/useTableSettings';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';

import {
  DEFAULT_LIMIT_ENDPOINTS,
  ENDPOINT_MODEL_ACTION_KEYS,
  initialEndpointsTableSettings,
} from '../constants';
import { Endpoint, EndpointStatus, EndpointType } from '../types';
import { getStatusChip, getUpdatedFilters, testIsActiveStatus } from '../utils';

export const Endpoints = () => {
  const intl = useIntl();
  const { setSearchParams } = useRouting();
  const dispatch = useDispatch();

  const filters = useSelector(slices.webhooks.selectors.selectedFilters);

  const { tableSettings = initialEndpointsTableSettings } = useTableSettings(
    TableContextType.Endpoints,
  );

  const [sortBy, setSortBy] = useState<
    { id: string; desc: boolean } | undefined
  >(tableSettings.sortBy);

  const { data = { results: [], total: 0 }, isLoading: isLoadingEndpoints } =
    api.endpoints.getEndpoints.useQuery({
      sortBy,
    });

  const columns = [
    {
      key: 'url',
      title: intl.formatMessage({
        id: 'Endpoints.endpointUrlTitle',
        defaultMessage: 'Endpoint URL',
        description: 'Column title for endpoint URL',
      }),
      cellType: 'link',
      mapCellProps: (endpoint: Endpoint) => ({
        pathname: '/',
        text: endpoint.endpointUrl,
        onClick: () => {
          dispatch(slices.webhooks.actions.setActiveEndpoint(endpoint));
          dispatch(slices.webhooks.actions.setActiveEndpointType('view'));
        },
      }),
    },
    {
      key: 'status',
      title: intl.formatMessage({
        id: 'Endpoints.statusTitle',
        defaultMessage: 'Status',
        description: 'Column title for status',
      }),
      cellType: 'chips',
      mapCellProps: (endpoint: Endpoint) => {
        const { text, status } = getStatusChip(
          endpoint.status as EndpointStatus,
          intl,
        );
        return {
          chips: [
            {
              text,
              status,
            },
          ],
        };
      },
    },
    {
      key: 'description',
      title: intl.formatMessage({
        id: 'Endpoints.descriptionTitle',
        defaultMessage: 'Description',
        description: 'Column title for description',
      }),
      cellType: 'text',
      mapCellProps: (endpoint: Endpoint) => ({
        text: endpoint.description,
        shouldTruncate: true,
      }),
    },
    {
      key: 'events',
      title: intl.formatMessage({
        id: 'Endpoints.selectedEventsTitle',
        defaultMessage: 'Selected Events',
        description: 'Column title for selected events',
      }),
      cellType: 'text',
      mapCellProps: (endpoint: Endpoint) => ({
        text: `${endpoint.events.length}`,
        isNumeric: true,
      }),
      width: 'm',
    },
    {
      key: 'dateCreated',
      title: intl.formatMessage({
        id: 'Endpoints.createdTitle',
        defaultMessage: 'Created',
        description: 'Column title for created date',
      }),
      cellType: 'datetime',
      mapCellProps: (endpoint: Endpoint) => ({
        datetime: endpoint.dateCreated,
        format: 'short_datetime',
      }),
      width: 'm',
    },
    {
      key: 'dateModified',
      title: intl.formatMessage({
        id: 'Endpoints.modifiedTitle',
        defaultMessage: 'Modified',
        description: 'Column title for modified date',
      }),
      cellType: 'datetime',
      mapCellProps: (endpoint: Endpoint) => ({
        datetime: endpoint.dateModified,
        format: 'short_datetime',
      }),
      width: 'm',
    },
    {
      key: 'createdBy',
      title: intl.formatMessage({
        id: 'Endpoints.createdByTitle',
        defaultMessage: 'Created by',
        description: 'Column title for created by user',
      }),
      minWidth: 'm',
      cellType: 'user',
      disableSortBy: true,
      mapCellProps: (endpoint: Endpoint) => ({
        asyncUser: {
          id: endpoint.createdBy,
          render: User,
        },
        mode: 'avatar-name',
      }),
    },
    {
      key: 'modifiedBy',
      title: intl.formatMessage({
        id: 'Endpoints.modifiedByTitle',
        defaultMessage: 'Modified by',
        description: 'Column title for modified by user',
      }),
      minWidth: 'm',
      cellType: 'user',
      disableSortBy: true,
      mapCellProps: (endpoint: Endpoint) => ({
        asyncUser: {
          id: endpoint.modifiedBy,
          render: User,
        },
        mode: 'avatar-name',
      }),
    },
  ];

  const handleRowAction = (endpoint: Endpoint, action: EndpointType) => {
    dispatch(slices.webhooks.actions.setActiveEndpoint(endpoint));
    dispatch(slices.webhooks.actions.setActiveEndpointType(action));
  };

  const handleUpdate = useCallback(
    (state: any, action?: any) => {
      if (action?.type === 'toggleSortBy') {
        const updatedSortBy = state.sortBy[0];

        setSortBy(updatedSortBy);
      }
    },
    [sortBy],
  );

  const getRowActions = useCallback(
    (endpoint: Endpoint) => {
      const { status } = endpoint;

      const modelActions = [
        {
          key: ENDPOINT_MODEL_ACTION_KEYS.VIEW_DETAILS,
          label: intl.formatMessage({
            id: 'Endpoints.viewDetailsAction',
            defaultMessage: 'view details',
            description: 'Action to view endpoint details',
          }),
        },
        {
          key: ENDPOINT_MODEL_ACTION_KEYS.VIEW_ATTEMPTS,
          label: intl.formatMessage({
            id: 'Endpoints.viewAttemptsAction',
            defaultMessage: 'view attempts',
            description: 'Action to view endpoint attempts',
          }),
        },
        {
          key: ENDPOINT_MODEL_ACTION_KEYS.EDIT,
          label: intl.formatMessage({
            id: 'Endpoints.editAction',
            defaultMessage: 'edit',
            description: 'Action to edit endpoint',
          }),
        },
        {
          key: testIsActiveStatus(status as EndpointStatus)
            ? ENDPOINT_MODEL_ACTION_KEYS.SUSPEND
            : ENDPOINT_MODEL_ACTION_KEYS.ACTIVATE,
          label: testIsActiveStatus(status as EndpointStatus)
            ? intl.formatMessage({
                id: 'Endpoints.suspendAction',
                defaultMessage: 'suspend',
                description: 'Action to suspend endpoint',
              })
            : intl.formatMessage({
                id: 'Endpoints.activateAction',
                defaultMessage: 'activate',
                description: 'Action to activate endpoint',
              }),
        },
        {
          key: ENDPOINT_MODEL_ACTION_KEYS.DELETE,
          label: intl.formatMessage({
            id: 'Endpoints.deleteAction',
            defaultMessage: 'delete',
            description: 'Action to delete endpoint',
          }),
        },
      ];

      const disabled = isLoadingEndpoints;

      return modelActions.map((action) => {
        return {
          key: action.key,
          disabled,
          label: capitalize(action.label),
          tooltip: disabled
            ? intl.formatMessage({
                id: 'Endpoints.loadingEndpointTooltip',
                defaultMessage: 'Loading endpoint…',
                description: 'Tooltip shown when endpoint is loading',
              })
            : undefined,
          onClick: () => {
            if (action.key === ENDPOINT_MODEL_ACTION_KEYS.VIEW_DETAILS) {
              handleRowAction(endpoint, 'view');
            } else if (
              action.key === ENDPOINT_MODEL_ACTION_KEYS.VIEW_ATTEMPTS
            ) {
              const updatedFilters = getUpdatedFilters({
                filters,
                endpoint,
              });
              dispatch(slices.webhooks.actions.setFilters(updatedFilters));
              setSearchParams({ secondaryTab: 'attempts' });
            } else {
              handleRowAction(endpoint, action.key as EndpointType);
            }
          },
        };
      });
    },
    [isLoadingEndpoints, data, intl],
  );

  return (
    <Layout preset="sections">
      {data.total > DEFAULT_LIMIT_ENDPOINTS && (
        <StatusMessage
          message={intl.formatMessage({
            id: 'Endpoints.resultsLimitedMessage',
            defaultMessage:
              'Results are limited to 500. To see more results, you will have to delete some endpoints.',
            description: 'Warning message about endpoint results limit',
          })}
          status="warning"
        />
      )}
      <PersistedTable
        columns={columns}
        context={TableContextType.Endpoints}
        data={data.results}
        getRowActions={getRowActions}
        isLoading={isLoadingEndpoints}
        options={{
          enableManageColumns: true,
          enableSelectRows: false,
          enableSearch: true,
          enablePagination: false,
          pageSizes: [],
        }}
        onUpdate={handleUpdate}
        reactTableOptions={{
          manualSortBy: true,
        }}
        state={{
          sortBy: [sortBy],
        }}
        totalCount={data.total}
      />
    </Layout>
  );
};
